import React, { useState, useEffect } from 'react';
import { useData } from '../../context/DataContext';
import { QuestionPerformanceChart } from './QuestionPerformanceChart';
import { LODetailedTable } from './LODetailedTable';
import { CurriculumDrilldownModal } from './CurriculumDrilldownModal';

export const CurriculumAnalysisTab = ({ onViewReport, onOpenQuestionPaper }) => {
  const { allStudentsData } = useData();
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('english');
  const [activeDrilldown, setActiveDrilldown] = useState(null);

  const uniqueGrades = [...new Set(allStudentsData.map(s => s.registeredGrade))].sort((a, b) => a - b);

  useEffect(() => {
    if (uniqueGrades.length > 0 && selectedGrade === 'all') {
      setSelectedGrade(uniqueGrades[0]);
    }
  }, [uniqueGrades, selectedGrade]);

  const handleQuestionClick = (qNum) => {
    setActiveDrilldown({ type: 'question', value: qNum, subject: selectedSubject });
  };

  const handleLOClick = (loName, subject) => {
    setActiveDrilldown({ type: 'lo', value: loName, subject });
  };

  return (
    <section id="curriculum-analysis" className="section-content">
      {/* Section Header */}
      <div className="mb-2">
        <h2 className="text-2xl font-bold text-gray-800 m-0">
          Performance breakdown by Learning outcomes
        </h2>
        <p className="text-sm text-gray-600 m-0">
          Performance breakdown by Learning Objective (LO) from the mapped curriculum
        </p>
      </div>

      {/* Card 1: Question-wise Performance (Q1 - Q20) */}
      <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100 mb-8 select-none">
        <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-1">
              Question-wise Performance (Q1 - Q20)
            </h3>
            <p className="text-sm text-gray-600">
              Average percentage of correct answers per question. Click bar to view students.
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <button
              onClick={() => onOpenQuestionPaper(selectedGrade)}
              className="flex items-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-bold text-sm transition-colors mr-2 cursor-pointer"
            >
              <span>📄</span>{' '}
              <span>
                {selectedGrade !== 'all' ? `View Grade ${selectedGrade} Paper` : 'View Paper Repository'}
              </span>
            </button>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none cursor-pointer"
            >
              <option value="all">All Subjects</option>
              <option value="english">English</option>
              <option value="math">Mathematics</option>
            </select>
            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none cursor-pointer"
            >
              <option value="all">All Grades</option>
              {uniqueGrades.map(g => (
                <option key={g} value={g}>
                  Grade {g}
                </option>
              ))}
            </select>
          </div>
        </div>

        <QuestionPerformanceChart
          selectedGrade={selectedGrade}
          selectedSubject={selectedSubject}
          onQuestionClick={handleQuestionClick}
        />
      </div>

      {/* Card 2: Detailed LO Performance Breakdown */}
      <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100 mb-8 select-none">
        <div className="mt-2">
          <h4 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
            <span>📋</span> Detailed LO Performance Breakdown
          </h4>
          <p className="text-xs text-gray-500 mb-6">
            Learning Objectives sorted by performance percentage in ascending order. Click any row below to view specific student details.
          </p>

          <LODetailedTable
            selectedGrade={selectedGrade}
            selectedSubject={selectedSubject}
            onLOClick={handleLOClick}
          />
        </div>
      </div>

      {/* Inline Analysis Drill-down container */}
      {activeDrilldown && (
        <CurriculumDrilldownModal
          filter={activeDrilldown}
          onClose={() => setActiveDrilldown(null)}
          onViewReport={(student) => {
            onViewReport(student);
          }}
        />
      )}
    </section>
  );
};
export default CurriculumAnalysisTab;
